import type { GitpodAPI } from "@/api";
import { toPlainAgentExecution, type PlainAgentExecution } from "@/queries/agent-queries";
import { createBatchLoader, type BatchLoader } from "@/queries/dataloader";
import { autoPaginate } from "@/queries/pagination";
import { agentExecutionEnvironmentIds } from "@/utils/agent-executions";

export type AgentExecutionLoader = BatchLoader<PlainAgentExecution[]>;

const createAgentExecutionLoader = (api: GitpodAPI, userId: string): AgentExecutionLoader =>
    createBatchLoader(async (environmentIds) => {
        const allExecutions = await autoPaginate(
            (params) => api.agentService.listAgentExecutions(params),
            { filter: { environmentIds, creatorIds: [userId] } },
            (response) => response.agentExecutions,
            100,
        );

        // Group by environment ID
        const results = new Map<string, PlainAgentExecution[]>();
        for (const exec of allExecutions) {
            const plain = toPlainAgentExecution(exec);
            for (const envId of agentExecutionEnvironmentIds(plain)) {
                if (!environmentIds.includes(envId)) {
                    continue;
                }
                const list = results.get(envId) ?? [];
                list.push(plain);
                results.set(envId, list);
            }
        }

        // Ensure every requested key has an entry (empty array = no executions for that env)
        for (const envId of environmentIds) {
            if (!results.has(envId)) {
                results.set(envId, []);
            }
        }

        return results;
    }, 25);

/**
 * Module-level singleton loader, shared across all components.
 *
 * Every component that calls getAgentExecutionLoader() gets the same instance
 * (per user), enabling cross-component batching within a render frame.
 */
let sharedLoader: { loader: AgentExecutionLoader; userId: string; api: GitpodAPI } | null = null;

export const getAgentExecutionLoader = (api: GitpodAPI, userId: string): AgentExecutionLoader => {
    if (!sharedLoader || sharedLoader.userId !== userId || sharedLoader.api !== api) {
        sharedLoader = {
            loader: createAgentExecutionLoader(api, userId),
            userId,
            api,
        };
    }

    return sharedLoader.loader;
};
